import React, { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  RouteProps,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import './styles/styles.scss';
// Booking flow stays EAGER — first-paint matters for paying-customer flow.
import BoxesComponent from './pages/Boxes';
import RemovalsComponent from './pages/Removals';
import RemovalsQuestionsComponent from './pages/RemovalsQuestions';
import RemovalsExtrasComponent from './pages/RemovalsExtras';
import CollectionDeliveryComponent from './pages/CollectionDelivery';
import PaymentComponent from './pages/Payment';
import BackToPaymentComponent from './pages/BackToPayment';
import SuccessComponent from './pages/Success';
import LandingComponent from './pages/Landing';
import LoginComponent from './pages/Login';
import RegistrationComponent from './pages/Register';
import ForgotPasswordComponent from './pages/ForgotPassword';
import ResetPasswordComponent from './pages/ResetPassword';
import Header from './components/Header';
import TrustPilotWidget from './components/TrustPilotWidget';
import ChatLauncher from './components/ChatLauncher';
import UserContext, { User, UserState } from './contexts/UserContext';
import { getUser, clearUser, getStoredFlow } from './services/storage';
import { ThemeSwitcher } from './components/ThemeSwitcher';
import { axiosKK } from './services/networkRequest';
import { Role } from './utilities/Role';
import { RouteComponentProps } from 'react-router';
import { jwtDecode } from 'jwt-decode';
import OxfordPackagesComponent from './pages/OxfordPackages';
import Footer from './components/Footer';
import CanonicalTag from './components/CanonicalTag';
import useGtmEventTracking from './components/useGtmEventTracking';
import { HelmetProvider } from 'react-helmet-async';
import KlarnaReturnComponent from './pages/KlarnaReturn';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded shells — split out of the customer first-paint bundle.
// Customer dashboard (User/*) — only paying-customer post-success need these.
const UserDashboardComponent = lazy(() => import('./pages/User/Dashboard'));
const UserOrderComponent = lazy(() => import('./pages/User/Order'));
const UserOrderEditComponent = lazy(() => import('./pages/User/OrderEdit'));
const UserProfileComponent = lazy(() => import('./pages/User/Profile'));
const UserPaymentsComponent = lazy(() => import('./pages/User/Payments'));
const UserUpdatePaymentComponent = lazy(() => import('./pages/User/UpdatePayment'));
const UserPastOrdersComponent = lazy(() => import('./pages/User/PastOrders'));
// Admin shells — booking customer never loads any of these.
const AdminDashboardComponent = lazy(() => import('./pages/Admin/Dashboard'));
const AdminUserComponent = lazy(() => import('./pages/Admin/User'));
const AdminUsersListComponent = lazy(() => import('./pages/Admin/Users'));
const AdminUsersCreateComponent = lazy(() => import('./pages/Admin/UsersCreate'));
const AdminOrderComponent = lazy(() => import('./pages/Admin/Order'));
const AdminOrderEditComponent = lazy(() => import('./pages/Admin/OrderEdit'));
const AdminCustomerComponent = lazy(() => import('./pages/Admin/Customer'));
const AdminCustomersComponent = lazy(() => import('./pages/Admin/Customers'));
const AdminDiscountComponent = lazy(() => import('./pages/Admin/Discounts'));
const AdminProductComponent = lazy(() => import('./pages/Admin/Product'));
const AdminProductsComponent = lazy(() => import('./pages/Admin/Products'));
const AdminRemovalQuoteDetailComponent = lazy(() => import('./pages/Admin/RemovalQuoteDetail'));
const AdminOxfordPackageOrderDetailComponent = lazy(() => import('./pages/Admin/OxfordPackageOrderDetail'));
const AdminLogisticsComponent = lazy(() => import('./pages/Admin/Logistics'));
const AdminPartnersComponent = lazy(() => import('./pages/Admin/Partners'));
const AdminInvoicesComponent = lazy(() => import('./pages/Admin/Invoices'));
// Partner portal self-config.
const PartnerConfigurationComponent = lazy(() => import('./pages/PartnerPortal/Configuration'));

const loginPath = '/login';
const adminDashboardPath = '/admin/dashboard';

function LoginRedirect() {
  return <Redirect to={loginPath} />;
}

// Customer-facing chrome (brand bar, convincer bar, TrustPilot, chat bubble)
// — hidden under /admin/*. Side-effect: toggles `body.admin-active` so the
// shared `--admin-header-offset` CSS variable collapses to 0 in admin (the
// admin shell + sticky thead + sidebar all read from that variable).
function AppChrome() {
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/admin');

  // useLayoutEffect runs before paint, so the --admin-header-offset variable
  // collapses to 0 in the same frame the chrome disappears. Plain useEffect
  // would leave a 155 px gap visible on the initial paint.
  useLayoutEffect(() => {
    if (isAdminRoute) {
      document.body.classList.add('admin-active');
    } else {
      document.body.classList.remove('admin-active');
    }
    return () => {
      document.body.classList.remove('admin-active');
    };
  }, [isAdminRoute]);

  if (isAdminRoute) return null;
  return (
    <>
      <Header />
      <TrustPilotWidget />
      <ChatLauncher />
    </>
  );
}

function AppFooter() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return <Footer />;
}

function AdminRedirect() {
  return <Redirect to={adminDashboardPath} />;
}

interface PrivateRouteProps extends RouteProps {
  user?: User;
  redirectComponent:
    | React.ComponentType<RouteComponentProps<any>>
    | React.ComponentType<any>;
  roles?: string[];
}

interface NoReturnRouteProps extends RouteProps {
  redirectPath: string;
}

function PrivateRoute({
  component: Component,
  user,
  redirectComponent,
  roles,
  ...props
}: PrivateRouteProps) {
  let allowed = user && (!roles || roles.includes(user.role));

  return (
    <Route {...props} component={allowed ? Component : redirectComponent} />
  );
}

function NoReturnRoute({
  component: Component,
  redirectPath,
  ...props
}: NoReturnRouteProps) {
  let allowed = useHistory().action !== 'POP';

  return (
    <Route
      {...props}
      component={allowed ? Component : () => <Redirect to={redirectPath} />}
    />
  );
}

function App() {
  let user: User | undefined = getUser() ?? undefined;

  if (user) {
    try {
      if (user.token) {
        const decodedUser = jwtDecode(user.token);
        const now = Date.now();
        const exp = (decodedUser.exp ?? 0) * 1000;

        if (exp < now) {
          clearUser();
          user = undefined;
        } else {
          axiosKK.defaults.headers.common['Authorization'] =
            'bearer ' + user.token;
        }
      } else {
        clearUser();
        user = undefined;
      }
    } catch (error) {
      // do nothing
    }
  }

  // Initialize GTM exactly once for the whole app
  useGtmEventTracking();

  const history = useHistory();
  const [userState, setUserState] = useState<UserState>({
    updateUser: () => {},
    user,
  });
  useEffect(() => {
    setUserState((u) => ({
      ...u,
      updateUser: (user: User) => {
        setUserState((u2) => ({
          ...u2,
          user,
        }));
      },
    }));

    // Load Hotjar tracking script
    const hotjarScriptId = 'hotjar-tracking';
    const hotjarScript = document.getElementById(hotjarScriptId);
    if (!hotjarScript) {
      const storedFlow = getStoredFlow();
      const hjid = storedFlow === 'Keepr' ? 6472223 : 6604580;
      const w = window as any;
      w.hj = w.hj || function () { (w.hj.q = w.hj.q || []).push(arguments); };
      w._hjSettings = { hjid, hjsv: 6 };
      const script = document.createElement('script');
      script.id = hotjarScriptId;
      script.async = true;
      script.src = `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=6`;
      document.head.appendChild(script);
    }
  }, []);

  return (
    <HelmetProvider>
    <Router>
      <UserContext.Provider value={userState}>
          <ThemeSwitcher />
          <CanonicalTag />
          <div className="App">
            <AppChrome />
            <section className="App-white-box">
              <ErrorBoundary>
              <Suspense fallback={null}>
              <Switch>
                <Route
                  exact={true}
                  component={() => (
                    <LoginComponent
                      postLogin={(user) => {
                        setUserState({
                          ...userState,
                          user,
                        });
                      }}
                    />
                  )}
                  path={loginPath}
                />

                <Route
                  exact={true}
                  component={() => (
                    <RegistrationComponent
                      postRegistration={() => history.push('/dashboard')}
                      path="/register"
                      createAdminAccount={false}
                      setUserState={setUserState}
                      userState={userState}
                    />
                  )}
                  path="/register"
                />
                <Route
                  exact={true}
                  component={ForgotPasswordComponent}
                  path="/forgot-password"
                />
                <Route
                  exact={true}
                  component={ResetPasswordComponent}
                  path="/password-reset/:token"
                />
                <Route exact={true} component={LandingComponent} path="/" />
                <Route
                  exact={true}
                  component={OxfordPackagesComponent}
                  path="/oxford-packages"
                />
                <Route
                  exact={true}
                  component={RemovalsComponent}
                  path="/removals"
                />
                <Route
                  exact={true}
                  component={RemovalsQuestionsComponent}
                  path="/removals/questions"
                />
                <Route
                  exact={true}
                  component={RemovalsExtrasComponent}
                  path="/removals/extras"
                />
                <Route
                  exact={true}
                  component={BoxesComponent}
                  path="/:route(storage|shipping|suitcase)/boxes"
                />
                <Route
                  exact={true}
                  component={BoxesComponent}
                  path="/:route(storage)/boxes/packages"
                />

                {/* Combined CollectionDelivery component replacing the separate ones */}
                <Route
                  exact={true}
                  component={CollectionDeliveryComponent}
                  path="/:route(storage|shipping|suitcase)/collection_address"
                />
                <Route
                  exact={true}
                  component={CollectionDeliveryComponent}
                  path="/removals/collection_address"
                />

                {/* Note: Confirmation route is removed */}

                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/:route(storage|shipping|suitcase)/payment"
                />
                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/:route(storage|shipping|suitcase)/payment/3ds-secure"
                />
                <Route
                  exact={true}
                  component={BackToPaymentComponent}
                  path="/:route(storage|shipping|suitcase)/back-to-payment"
                />
                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/removals/payment"
                />
                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/removals/payment/3ds-secure"
                />
                <Route
                  exact={true}
                  component={BackToPaymentComponent}
                  path="/removals/back-to-payment"
                />

                <Route
                  exact={true}
                  component={KlarnaReturnComponent}
                  path="/:route(storage|shipping|suitcase)/klarna-return"
                />
                <Route
                  exact={true}
                  component={KlarnaReturnComponent}
                  path="/removals/klarna-return"
                />
                <Route
                  exact={true}
                  component={KlarnaReturnComponent}
                  path="/oxford-packages/klarna-return"
                />

                <NoReturnRoute
                  exact={true}
                  redirectPath="/dashboard"
                  component={SuccessComponent}
                  path="/:route(storage|shipping|suitcase|stannes)/success"
                />
                <NoReturnRoute
                  exact={true}
                  redirectPath="/dashboard"
                  component={SuccessComponent}
                  path="/removals/success"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserDashboardComponent}
                  path="/dashboard"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserPastOrdersComponent}
                  path="/past-orders"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserOrderComponent}
                  path="/order/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserOrderComponent}
                  path="/removals/order/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserOrderEditComponent}
                  path="/order/:id/edit"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserProfileComponent}
                  path="/profile"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserPaymentsComponent}
                  path="/payments"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserUpdatePaymentComponent}
                  path="/update-payment-method"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminDashboardComponent}
                  path="/admin/dashboard"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminUserComponent}
                  path="/admin/user/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminUsersListComponent}
                  path="/admin/users"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminUsersCreateComponent}
                  path="/admin/users/create"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminOrderComponent}
                  path="/admin/order/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminRemovalQuoteDetailComponent}
                  path="/admin/removals/quote/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminOxfordPackageOrderDetailComponent}
                  path="/admin/oxford-packages/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminOrderEditComponent}
                  path="/admin/order/:id/edit"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminCustomersComponent}
                  path="/admin/customers"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminCustomerComponent}
                  path="/admin/customer/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminDiscountComponent}
                  path="/admin/discounts"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminProductComponent}
                  path="/admin/product/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminProductsComponent}
                  path="/admin/products"
                />
                {/* Consolidated Logistics page — single tabbed view covering
                    Orders / Route Plan / Warehouse / Forecast. Legacy paths
                    resolve to the right tab inside the page. */}
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics/route-plan"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics/warehouse"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics/forecast"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/route-plan"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/warehouse"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/warehouse-forecast"
                />

                {/* Consolidated Partners page — single tabbed view covering
                    Partners list / User Access / per-partner Pricing / Vehicles
                    / Service Areas. Legacy paths resolve to the right tab. */}
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partner-access"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners/:partnerId/pricing"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners/:partnerId/vehicles"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners/:partnerId/service-areas"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners/:partnerId/warehouses"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminInvoicesComponent}
                  path="/admin/invoices"
                />

                {/* Partner portal — self-configuration screens for partner
                    users (Master / Driver / Warehouse). The page reads the
                    user's role + partnerId from UserContext and gates the
                    tab list accordingly. Authenticated; the component
                    handles its own redirect-to-login if no user. */}
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={PartnerConfigurationComponent}
                  path="/partner/configuration"
                />

                <Route exact={true} component={NotFound} />
              </Switch>
              </Suspense>
              </ErrorBoundary>
            </section>
            <AppFooter />
          </div>
      </UserContext.Provider>
    </Router>
    </HelmetProvider>
  );
}

function NotFound(): JSX.Element {
  return <h2>Unknown</h2>;
}

export default App;
